import { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl, { Map as MaplibreMap, Marker, type StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { spatialApi, layersApi, type SpatialQueryResult, type ParcelWithPrice, type LayerStyle } from '@/api/mapApi';
import { fetchGeoJSON } from '@/utils/geojsonCache';
import { booleanPointInPolygon, point as turfPoint } from '@turf/turf';

interface MapContainerProps {
  center?: { lat: number; lng: number };
  zoom?: number;
  onMapClick?: (lat: number, lng: number) => void;
  onResults?: (result: SpatialQueryResult | null) => void;
  onLoadingChange?: (loading: boolean) => void;
  onMapReady?: (map: MaplibreMap) => void;
}

const DEFAULT_CENTER: [number, number] = [105.8542, 21.0285];  // Hanoi
const DEFAULT_ZOOM = 15;

// OSM raster style — free for local dev. For production use MapTiler/Mapbox.
const OSM_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    'osm-raster': {
      type: 'raster',
      tiles: [
        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
      maxzoom: 19,
    },
  },
  layers: [
    { id: 'osm-tiles', type: 'raster', source: 'osm-raster', minzoom: 0, maxzoom: 22 },
  ],
};

export function MapContainer({ center, zoom = DEFAULT_ZOOM, onMapClick, onResults, onLoadingChange, onMapReady }: MapContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const layerRegistry = useRef<Map<string, string>>(new Map());  // layer.id -> source.id
  const parcelSourceId = 'parcels-source';
  const markerRef = useRef<Marker | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Init map
  useEffect(() => {
    if (!containerRef.current) return;
    try {
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: OSM_STYLE,
        center: center ? [center.lng, center.lat] : DEFAULT_CENTER,
        zoom,
        attributionControl: { compact: true },
      });
      mapRef.current = map;

      map.addControl(new maplibregl.NavigationControl(), 'top-right');
      map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');

      map.on('load', () => {
        setLoading(false);
        onMapReady?.(map);
      });

      map.on('click', async (e) => {
        const { lng, lat } = e.lngLat;
        onMapClick?.(lat, lng);
        onLoadingChange?.(true);

        // Place marker
        if (markerRef.current) markerRef.current.remove();
        markerRef.current = new maplibregl.Marker({ color: '#ef4444' })
          .setLngLat([lng, lat])
          .addTo(map);

        try {
          const result = await spatialApi.query(lat, lng);
          // Refine with client-side point-in-polygon (since server returns bbox candidates)
          const refined = refineWithPointInPolygon(result, lng, lat);
          await renderResultOnMap(map, refined, layerRegistry.current, parcelSourceId);
          onResults?.(refined);
        } catch (err) {
          console.error('Spatial query failed:', err);
          onResults?.(null);
        } finally {
          onLoadingChange?.(false);
        }
      });

      map.on('error', (e) => {
        console.error('MapLibre error:', e);
      });
    } catch (err) {
      console.error('Failed to init map:', err);
      setError('Không thể khởi tạo bản đồ');
      setLoading(false);
    }
    return () => {
      mapRef.current?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update center/zoom
  useEffect(() => {
    if (mapRef.current && center) {
      mapRef.current.flyTo({ center: [center.lng, center.lat], zoom, duration: 1500 });
    }
  }, [center?.lat, center?.lng, zoom]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-red-50 p-6">
        <div className="text-center max-w-md">
          <p className="text-red-600 font-semibold mb-2">⚠️ Lỗi tải bản đồ</p>
          <p className="text-sm text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-3"></div>
            <p className="text-sm text-gray-600">Đang tải bản đồ...</p>
          </div>
        </div>
      )}
      <div ref={containerRef} className="w-full h-full" />
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs text-gray-600 shadow-sm z-[1]">
        Dữ liệu mang tính tham khảo, không có giá trị pháp lý
      </div>
    </div>
  );
}

// Render spatial query result on the map
async function renderResultOnMap(
  map: MaplibreMap,
  result: SpatialQueryResult,
  layerRegistry: Map<string, string>,
  parcelSourceId: string
) {
  // Clear previous project layers
  for (const [id, sourceId] of layerRegistry) {
    if (map.getLayer(id)) map.removeLayer(id);
    if (map.getSource(sourceId)) map.removeSource(sourceId);
  }
  layerRegistry.clear();
  if (map.getLayer('parcels-fill')) map.removeLayer('parcels-fill');
  if (map.getLayer('parcels-line')) map.removeLayer('parcels-line');
  if (map.getSource(parcelSourceId)) map.removeSource(parcelSourceId);

  // Render each project's layers
  let layerOrder = 0;
  for (const project of result.projects) {
    for (const layer of project.layers) {
      const sourceId = `lyr-${layer.id}`;
      layerRegistry.set(layer.id, sourceId);
      try {
        const url = layersApi.getGeoJSONUrl(layer.id);
        const { data: geojson } = await fetchGeoJSON(url);
        map.addSource(sourceId, { type: 'geojson', data: geojson as any });
        const style = styleToPaint(layer.style);
        const lineLayerId = `${sourceId}-line`;
        const fillLayerId = `${sourceId}-fill`;
        map.addLayer({
          id: lineLayerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': style.lineColor,
            'line-width': style.lineWidth,
            'line-opacity': style.lineOpacity,
          },
        });
        map.addLayer({
          id: fillLayerId,
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': style.fillColor,
            'fill-opacity': style.fillOpacity,
          },
        });
        layerOrder += 2;
      } catch (err) {
        console.error(`Failed to load layer ${layer.id}:`, err);
      }
    }
  }

  // Render parcels
  if (result.parcels.length > 0) {
    const features = result.parcels
      .filter((p) => p.geometry)
      .map((p) => ({
        type: 'Feature' as const,
        geometry: p.geometry!,
        properties: {
          id: p.id,
          parcelCode: p.parcelCode,
          address: p.address,
          landType: p.landType,
          areaSqm: p.areaSqm,
        },
      }));
    if (features.length > 0) {
      map.addSource(parcelSourceId, { type: 'geojson', data: { type: 'FeatureCollection', features } as any });
      map.addLayer({
        id: 'parcels-fill',
        type: 'fill',
        source: parcelSourceId,
        paint: {
          'fill-color': ['get', 'landTypeColor'] as any,
          'fill-opacity': 0.3,
        },
      });
      map.addLayer({
        id: 'parcels-line',
        type: 'line',
        source: parcelSourceId,
        paint: {
          'line-color': '#374151',
          'line-width': 1,
        },
      });
    }
  }
}

// Refine server bbox results with precise point-in-polygon
function refineWithPointInPolygon(result: SpatialQueryResult, lng: number, lat: number): SpatialQueryResult {
  const pt = turfPoint([lng, lat]);
  const refinedParcels = result.parcels.filter((p) => {
    if (!p.geometry) return false;
    try {
      // Build a minimal Feature for turf
      const feat = { type: 'Feature' as const, geometry: p.geometry, properties: {} };
      return booleanPointInPolygon(pt, feat as any);
    } catch {
      return false;
    }
  });
  return { ...result, parcels: refinedParcels };
}

function styleToConfig(style: LayerStyle) {
  return {
    lineColor: style.strokeColor ?? '#3388ff',
    lineWidth: style.strokeWeight ?? 2,
    lineOpacity: style.strokeOpacity ?? 1,
    fillColor: style.fillColor ?? '#3388ff',
    fillOpacity: style.fillOpacity ?? 0.2,
  };
}

const styleToPaint = styleToConfig;

// Helper to apply land type colors (could be a data-driven style)
export const landTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    dat_o: '#ef4444',
    dat_nong_nghiep: '#84cc16',
    dat_cong: '#3b82f6',
    dat_san_xuat: '#f59e0b',
    dat_giao_thong: '#6b7280',
    dat_thuy_loi: '#06b6d4',
    dat_rung: '#16a34a',
    dat_khac: '#a855f7',
  };
  return colors[type] ?? '#9ca3af';
};
